import { useRef, useEffect, useState } from 'react'
import { Icon, IconName } from '@/components/Icon'
import { Tag } from '@/components/Tag'
import { Button } from '@/components/Button'
import { Link } from '@/components/Link'
import { ContactLink } from '@/components/ContactLink'
import { Tooltip } from '@/components/Tooltip'
import { leaseDebug } from '@/utils/leaseDebug'
import { linkTargetProps } from '@/utils/linkTarget'

interface UnitTag {
	label: string
	color: 'brand' | 'success' | 'content2' | 'warning' | 'danger'
	iconLeft?: IconName
}

export type UnitCardLayout = 'default' | 'compact'
export type UnitCardPricing = 'flat' | 'revenue-management' | 'hidden'

export interface UnitCardProps {
	unitNumber: string
	/** Prefix before the unit number; empty string hides it. @default "Unit" */
	unitLabel?: string
	/** Omitted in portfolio mode, where the community name replaces the plan link. */
	floorPlanName?: string
	floorPlanHref?: string
	imageUrl?: string
	beds: string
	baths: string
	sqft: number
	availableDate?: string
	/** Replaces the available date with custom text (e.g. on-notice masking). */
	availabilityOverride?: string
	priceRange?: string
	baseRent?: string
	leaseTerm?: string
	tags?: UnitTag[]
	specialMessage?: string
	hasVirtualTour?: boolean
	tourUrl?: string
	onTourClick?: () => void
	/** Pricing display mode. @default "flat" */
	pricing?: UnitCardPricing
	/** Whether fees are included in the total price. Changes pricing layout. @default true */
	hasFees?: boolean
	/** Show the disclaimer asterisk in pricing. @default true */
	hasDisclaimerSymbol?: boolean
	/** Community name displayed below the floor plan link; plain text when there is no href. */
	communityName?: string
	communityHref?: string
	communityTarget?: string
	/** Card layout variant. `"compact"` renders a horizontal card for map popups. */
	layout?: UnitCardLayout
	/** Visually highlights the card (e.g. when the corresponding map unit is hovered). */
	highlighted?: boolean
	onClick?: () => void
	onFloorPlanClick?: () => void
	onPricingClick?: () => void
	onLeaseTermClick?: () => void
	onHoverStart?: () => void
	onHoverEnd?: () => void
	spacesClass?: string
	className?: string
}

export function UnitCard ({
	unitNumber,
	unitLabel = 'Unit',
	floorPlanName,
	floorPlanHref = '#',
	imageUrl,
	beds,
	baths,
	sqft,
	availableDate,
	availabilityOverride,
	priceRange,
	baseRent,
	leaseTerm,
	tags = [],
	specialMessage,
	hasVirtualTour = false,
	tourUrl,
	onTourClick,
	pricing = 'flat',
	hasFees = true,
	hasDisclaimerSymbol = true,
	communityName,
	communityHref,
	communityTarget,
	layout = 'default',
	highlighted = false,
	onClick,
	onFloorPlanClick,
	onPricingClick,
	onLeaseTermClick,
	onHoverStart,
	onHoverEnd,
	spacesClass = '',
	className = ''
}: UnitCardProps) {
	const effectiveTags: UnitTag[] = specialMessage
		? [{ label: 'Special', color: 'success', iconLeft: 'Tag' as const }, ...tags]
		: tags

	if (layout === 'compact') {
		return (
			<CompactUnitCard
				imageUrl={imageUrl}
				tags={effectiveTags}
				beds={beds}
				baths={baths}
				sqft={sqft}
				availableDate={availableDate}
				availabilityOverride={availabilityOverride}
				priceRange={priceRange}
				baseRent={baseRent}
				leaseTerm={leaseTerm}
				pricing={pricing}
				hasFees={hasFees}
				hasDisclaimerSymbol={hasDisclaimerSymbol}
				className={className}
			/>
		)
	}

	const highlightClasses = highlighted ? 'spaces:border-brand-base spaces:shadow-sm-below' : ''
	const cardClasses = [
		'spaces-unit spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:overflow-hidden spaces:flex spaces:flex-col spaces:cursor-pointer spaces:group spaces:transition-all spaces:duration-200 spaces:hover:shadow-sm-below spaces:outline-none spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
		highlightClasses,
		highlighted && 'spaces-highlighted',
		spacesClass,
		className
	]
		.filter(Boolean)
		.join(' ')

	return (
		<>
			<div
				className={cardClasses}
				tabIndex={0}
				onClick={onClick}
				onKeyDown={e => {
					if ((e.key === 'Enter' || e.key === ' ') && onClick) {
						e.preventDefault()
						onClick()
					}
				}}
				onMouseEnter={onHoverStart}
				onMouseLeave={onHoverEnd}
			>
				{/* Image */}
				<div className='spaces-unit-image spaces:relative spaces:aspect-square spaces:w-full spaces:overflow-hidden spaces:bg-image-bg'>
					{imageUrl && (
						<img
							src={imageUrl}
							alt={`Floor plan for unit ${unitNumber}`}
							className='spaces:absolute spaces:inset-0 spaces:w-full spaces:h-full spaces:object-contain'
						/>
					)}
					{/* Overlay: tags + virtual tour */}
					<div className='spaces:absolute spaces:inset-0 spaces:p-12 spaces:flex spaces:items-start spaces:gap-8'>
						<div className='spaces:flex spaces:flex-1 spaces:flex-wrap spaces:gap-8 spaces:min-w-0'>
							{effectiveTags.map((tag, i) => (
								<Tag
									key={i}
									label={tag.label}
									color={tag.color}
									appearance='subtle'
									size='sm'
									iconLeft={tag.iconLeft}
								/>
							))}
						</div>
						{hasVirtualTour && (
							<Tooltip label='Virtual Tour' placement='bottom'>
								<Button
									iconOnly='Tour360'
									appearance='stroke'
									color='content4'
									size='md'
									aria-label='Virtual Tour'
									onClick={e => {
										e.stopPropagation()
										onTourClick?.()
									}}
								/>
							</Tooltip>
						)}
					</div>
				</div>

				{/* Details */}
				<div className='spaces-unit-details spaces:flex spaces:flex-col spaces:gap-8 spaces:p-16'>
					{/* Name */}
					<div className='spaces:flex spaces:flex-col'>
						<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:text-18/24 spaces:font-semi spaces:text-content-1'>
							{unitLabel && <span>{unitLabel}</span>}
							<span className='spaces:flex-1'>{unitNumber}</span>
						</div>
						{floorPlanName && (
							<Link
								href={floorPlanHref}
								label={floorPlanName}
								defaultUnderline={false}
								color='brand'
								onClick={e => { e.stopPropagation(); if (onFloorPlanClick) { e.preventDefault(); onFloorPlanClick() } }}
							/>
						)}
						{communityName && (communityHref ? (
							<Link
								href={communityHref}
								label={communityName}
								{...linkTargetProps(communityTarget)}
								defaultUnderline={false}
								color='brand'
								spacesClass='spaces-unit-community'
								onClick={e => { e.stopPropagation() }}
								onKeyDown={e => { e.stopPropagation() }}
							/>
						) : (
							<span className='spaces-unit-community spaces:text-16/24 spaces:font-default spaces:text-content-2'>{communityName}</span>
						))}
					</div>

					{/* Attributes */}
					<Attributes
						beds={beds}
						baths={baths}
						sqft={sqft}
						availableDate={availableDate}
						availabilityOverride={availabilityOverride}
						spacesClass='spaces-unit-attributes'
					/>
				</div>

				{/* Pricing */}
				<div
					className={`spaces-unit-pricing spaces:mt-auto spaces:flex spaces:items-center spaces:gap-8 spaces:px-16 spaces:bg-canvas-4 spaces:border-t spaces:border-canvas-3 ${
						pricing === 'hidden' ? 'spaces:py-24' : 'spaces:py-16'
					}`}
				>
					<PricingBlock
						pricing={pricing}
						hasFees={hasFees}
						hasDisclaimerSymbol={hasDisclaimerSymbol}
						priceRange={priceRange}
						baseRent={baseRent}
						leaseTerm={leaseTerm}
						onLeaseTermClick={onLeaseTermClick}
					/>
					{pricing !== 'hidden' && onPricingClick && (
						<Tooltip label='Calculate My Cost' placement='left'>
							<Button
								iconOnly='Calculator'
								appearance='subtle'
								color='content4'
								size='md'
								aria-label='Calculate My Cost'
								onClick={e => {
									e.stopPropagation()
									onPricingClick()
								}}
							/>
						</Tooltip>
					)}
				</div>
			</div>
		</>
	)
}

/* ── Compact layout for map hover popup ─────────────────────── */

interface CompactUnitCardProps {
	imageUrl?: string
	tags: UnitTag[]
	beds: string
	baths: string
	sqft: number
	availableDate?: string
	availabilityOverride?: string
	priceRange?: string
	baseRent?: string
	leaseTerm?: string
	pricing: UnitCardPricing
	hasFees: boolean
	hasDisclaimerSymbol: boolean
	spacesClass?: string
	className?: string
}

function CompactUnitCard ({
	imageUrl,
	tags,
	beds,
	baths,
	sqft,
	availableDate,
	availabilityOverride,
	priceRange,
	baseRent,
	leaseTerm,
	pricing,
	hasFees,
	hasDisclaimerSymbol,
	spacesClass = '',
	className = ''
}: CompactUnitCardProps) {
	const contentRef = useRef<HTMLDivElement>(null)
	const [imageWidth, setImageWidth] = useState<number>(0)

	useEffect(() => {
		const el = contentRef.current
		if (!el) return

		const observer = new ResizeObserver(([entry]) => {
			setImageWidth(entry.contentRect.height)
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [])

	return (
		<div
			className={`spaces-unit spaces-compact spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:overflow-hidden spaces:w-[440px] spaces:shadow-sm-below spaces:flex ${spacesClass} ${className}`}
		>
			{/* Image — square: width matches content column height */}
			<div
				className='spaces-unit-image spaces:relative spaces:shrink-0 spaces:border-r spaces:border-canvas-3 spaces:bg-image-bg'
				style={{ width: imageWidth || undefined }}
			>
				{imageUrl && (
					<img
						src={imageUrl}
						alt=''
						className='spaces:absolute spaces:inset-0 spaces:w-full spaces:h-full spaces:object-contain'
					/>
				)}
				{/* Tags overlay on image */}
				{tags.length > 0 && (
					<div className='spaces:absolute spaces:inset-0 spaces:p-12 spaces:flex spaces:items-start'>
						<div className='spaces:flex spaces:flex-1 spaces:flex-wrap spaces:gap-8 spaces:min-w-0'>
							{tags.map((tag, i) => (
								<Tag
									key={i}
									label={tag.label}
									color={tag.color}
									appearance='subtle'
									size='sm'
									iconLeft={tag.iconLeft}
								/>
							))}
						</div>
					</div>
				)}
			</div>

			{/* Content */}
			<div ref={contentRef} className='spaces-unit-details spaces:flex spaces:flex-col spaces:flex-1 spaces:min-w-0'>
				{/* Attributes */}
				<div className='spaces:p-16'>
					<Attributes
						beds={beds}
						baths={baths}
						sqft={sqft}
						availableDate={availableDate}
						availabilityOverride={availabilityOverride}
						spacesClass='spaces-unit-attributes'
					/>
				</div>

				{/* Pricing */}
				<div
					className={`spaces-unit-pricing spaces:px-16 spaces:bg-canvas-4 spaces:border-t spaces:border-canvas-3 ${
						pricing === 'hidden' && hasFees ? 'spaces:py-24' : 'spaces:py-16'
					}`}
				>
					<PricingBlock
						pricing={pricing}
						hasFees={hasFees}
						hasDisclaimerSymbol={hasDisclaimerSymbol}
						priceRange={priceRange}
						baseRent={baseRent}
						leaseTerm={leaseTerm}
						contactAsLink={false}
					/>
				</div>
			</div>
		</div>
	)
}

/* ── Shared sub-components ──────────────────────────────────── */

interface AttributesProps {
	beds: string
	baths: string
	sqft: number
	availableDate?: string
	availabilityOverride?: string
	spacesClass?: string
}

function Attributes ({
	beds,
	baths,
	sqft,
	availableDate,
	availabilityOverride,
	spacesClass = ''
}: AttributesProps) {
	/*
	 * Divider approach: every item carries a `border-l` on its left edge.
	 * The inner flex row is shifted left by -13px (1px border + 12px padding),
	 * and the outer wrapper clips with `overflow-hidden`. This causes the first
	 * item on *every* flex line to have its border and left padding clipped,
	 * leaving only true dividers between adjacent items on the same line.
	 * When `availableDate` wraps to a second line it becomes line-first and its
	 * border is clipped too — no orphaned divider on either side.
	 */
	return (
		<div className={`spaces:overflow-hidden ${spacesClass}`}>
			<div className='spaces:-ml-13 spaces:flex spaces:flex-wrap spaces:items-center spaces:gap-y-4'>
				<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
					<Icon name='Bed' size={24} className='spaces:text-content-4' />
					<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>{beds}</span>
				</div>
				<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
					<Icon name='Bath' size={24} className='spaces:text-content-4' />
					<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>{baths}</span>
				</div>
				<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
					<Icon name='Area' size={24} className='spaces:text-content-4' />
					<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>
						{sqft.toLocaleString()} ft<sup className='spaces:text-10 spaces:font-strong'>2</sup>
					</span>
				</div>
				{availableDate && (
					<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
						<Icon name='Calendar' size={24} className='spaces:text-content-4' />
						<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>
							Avail. {availableDate}
						</span>
					</div>
				)}
				{!availableDate && availabilityOverride && (
					<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
						<Icon name='Calendar' size={24} className='spaces:text-content-4' />
						<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>
							{availabilityOverride}
						</span>
					</div>
				)}
			</div>
		</div>
	)
}

interface PricingBlockProps {
	pricing: UnitCardPricing
	hasFees: boolean
	hasDisclaimerSymbol: boolean
	priceRange?: string
	baseRent?: string
	leaseTerm?: string
	onLeaseTermClick?: () => void
	/** Render "Contact" as a link or plain text. @default true */
	contactAsLink?: boolean
}

function PricingBlock ({
	pricing,
	hasFees,
	hasDisclaimerSymbol,
	priceRange,
	baseRent,
	leaseTerm,
	onLeaseTermClick,
	contactAsLink = true
}: PricingBlockProps) {
	if (pricing === 'revenue-management') {
		leaseDebug('card-pricing-render', { priceRange, baseRent, leaseTerm })
	}

	/* ── Hidden pricing ──────────────────────────────────────── */
	if (pricing === 'hidden') {
		return <ContactLink postText='for Pricing' asLink={contactAsLink} />
	}

	const disclaimer = hasDisclaimerSymbol ? '*' : ''

	/* ── hasFees = false: single-line base rent primary ──────── */
	if (!hasFees) {
		return (
			<div className='spaces:@container spaces:flex spaces:flex-1 spaces:min-w-0 spaces:flex-col spaces:gap-4 spaces:@pricing-primary:flex-row spaces:@pricing-primary:items-center spaces:@pricing-primary:gap-8'>
				<div className='spaces:flex spaces:items-baseline spaces:gap-4 spaces:whitespace-nowrap'>
					<span className='spaces:text-18/24 spaces:font-strong spaces:text-content-1'>{baseRent}</span>
					<span className='spaces:text-14/16 spaces:font-default spaces:text-content-3'>{`Base Rent${disclaimer}`}</span>
				</div>
				{leaseTerm && pricing === 'flat' && (
					<>
						<span className='spaces:hidden spaces:@pricing-primary:block spaces:w-4 spaces:h-4 spaces:rounded-full spaces:bg-canvas-2 spaces:shrink-0' />
						<span className='spaces-unit-lease-term spaces:text-14/16 spaces:font-default spaces:text-content-3 spaces:whitespace-nowrap'>
							{leaseTerm}
						</span>
					</>
				)}
				{leaseTerm && pricing === 'revenue-management' && (
					<>
						<span className='spaces:hidden spaces:@pricing-primary:block spaces:w-4 spaces:h-4 spaces:rounded-full spaces:bg-canvas-2 spaces:shrink-0' />
						{onLeaseTermClick ? (
							<span className='spaces-unit-lease-term spaces:text-14/16 spaces:font-default spaces:whitespace-nowrap'>
								<Link
									onClick={e => {
										e.stopPropagation()
										onLeaseTermClick()
									}}
									label={leaseTerm}
									color='brand'
									defaultUnderline={false}
								/>
							</span>
						) : (
							<span className='spaces-unit-lease-term spaces:font-default spaces:text-14/16 spaces:text-content-3 spaces:whitespace-nowrap'>
								{leaseTerm}
							</span>
						)}
					</>
				)}
			</div>
		)
	}

	/* ── hasFees = true: two-line total price primary ────────── */
	return (
		<div className='spaces:@container spaces:flex spaces:flex-col spaces:flex-1 spaces:min-w-0'>
			<div className='spaces:flex spaces:items-baseline spaces:whitespace-nowrap'>
				<span className='spaces:text-18/24 spaces:font-strong spaces:text-content-1'>{priceRange}</span>
				<span className='spaces:text-14/16 spaces:font-default spaces:text-content-3'>
					/mo{disclaimer}
				</span>
			</div>
			{(baseRent || leaseTerm) && (
				<div className='spaces:flex spaces:flex-col spaces:gap-4 spaces:@pricing-detail:flex-row spaces:@pricing-detail:items-center spaces:@pricing-detail:gap-8 spaces:text-14/16 spaces:font-default spaces:text-content-3'>
					{baseRent && <span className='spaces:whitespace-nowrap'>{baseRent} Base Rent</span>}
					{baseRent &&
						leaseTerm &&
						(pricing === 'flat' || pricing === 'revenue-management') && (
							<span className='spaces:hidden spaces:@pricing-detail:block spaces:w-4 spaces:h-4 spaces:rounded-full spaces:bg-canvas-2 spaces:shrink-0' />
						)}
					{leaseTerm && pricing === 'flat' && (
						<span className='spaces-unit-lease-term spaces:whitespace-nowrap'>{leaseTerm}</span>
					)}
					{leaseTerm &&
						pricing === 'revenue-management' &&
						(onLeaseTermClick ? (
							<span className='spaces-unit-lease-term spaces:whitespace-nowrap'>
								<Link
									onClick={e => {
										e.stopPropagation()
										onLeaseTermClick()
									}}
									label={leaseTerm}
									color='brand'
									defaultUnderline={false}
									aria-label='Change lease term'
									role='button'
								/>
							</span>
						) : (
							<span className='spaces-unit-lease-term spaces:whitespace-nowrap'>{leaseTerm}</span>
						))}
				</div>
			)}
		</div>
	)
}
